import cx from 'classnames';
import {
  type KeyboardEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useOutsideAlerter} from '~/common/FabricInvitation/EventCreateDesign/hooks/useOutsideAlerter';
import {Button} from '~library/atoms/Button';
import {Input, type InputProps} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {TrashIcon} from '~library/svg/v2/TrashIcon';
import {link} from '~libSass/base/_exports.sass';
import './InputWithRemove.sass';

interface InputWithRemoveProps extends Omit<InputProps, 'data-qa-id' | 'ref'> {
  bold?: boolean;
  variant?: ButtonSize;
  dataQaId?: string;
  fullWidth?: boolean;
  onRemove?: () => void;
  onExitEditModeValidation?: (value: string) => boolean;
  onExitEditMode?: () => void;
  displayValueFn?: (value: string) => ReactNode;
  showIcon?: boolean;
  onClick?: () => void;
  shouldEnterExit?: boolean;
  shouldFocusOnMount?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  value?: string;
  error?: string | boolean;
  className?: string;
  id?: string;
  placeholder?: string;
}

export const InputWithRemove = ({
  bold = false,
  variant,
  dataQaId = '',
  fullWidth,
  onRemove,
  onExitEditModeValidation,
  onExitEditMode,
  displayValueFn,
  showIcon = true,
  onClick,
  shouldEnterExit = true,
  shouldFocusOnMount = false,
  value = '',
  ref,
  placeholder,
  ...props
}: InputWithRemoveProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [editMode, setEditMode] = useState(false);

  const onEditClick = useCallback(async () => setEditMode(true), []);

  const exitEditMode = useCallback(() => {
    const valid = onExitEditModeValidation ? onExitEditModeValidation(value) : true;
    if (valid) {
      if (onExitEditMode) {
        onExitEditMode();
      }
      setTimeout(() => setEditMode(false), 100);
    }
  }, [onExitEditMode, onExitEditModeValidation, value]);

  useOutsideAlerter(inputRef, exitEditMode);

  useEffect(() => {
    if (shouldFocusOnMount) {
      onEditClick();
    }
  }, [onEditClick, shouldFocusOnMount]);

  const onKeyDownHandler: KeyboardEventHandler = useCallback(
    (event) => {
      if (
        event.key === 'Tab' ||
        event.key === 'Escape' ||
        (event.key === 'Enter' && shouldEnterExit)
      ) {
        exitEditMode();
      }
    },
    [exitEditMode, shouldEnterExit]
  );

  return (
    <div
      id={props.id}
      className={cx(
        'input-with-remove__wrapper',
        {
          'input-with-remove__wrapper--bold': bold,
          'input-with-remove__wrapper--fullWidth': fullWidth,
          'input-with-remove__wrapper--edit-mode': editMode,
          'input-with-remove__wrapper--icon': showIcon && value,
        },
        props.className
      )}
      ref={ref}
    >
      {editMode && (
        <Input
          {...props}
          data-qa-id={`${dataQaId}-input-box`}
          ref={inputRef}
          className="real-input"
          onKeyDown={onKeyDownHandler}
          placeholder={placeholder}
          value={value}
          autoFocus
        />
      )}
      {!editMode && (
        <Button
          data-qa-id={dataQaId}
          variant="transparent"
          onClick={onClick ?? onEditClick}
          disableProcessing
          className={cx({
            'fake-input-button': true,
            error: props.error,
          })}
        >
          <div
            className={cx({
              'inline-input': true,
              error: props.error,
            })}
          >
            <Typography
              // biome-ignore lint/style/noNestedTernary: existing error
              variant={variant === 'large' ? 'header2' : bold ? 'paragraph1' : 'paragraph2'}
              className="inline-input__text"
              color={value ? 'charcoal' : 'coin'}
            >
              {displayValueFn && value ? displayValueFn(value) : value || placeholder}
            </Typography>
          </div>
          {props.error && typeof props.error === 'string' && (
            <span className="input-error">{props.error}</span>
          )}
        </Button>
      )}
      {value && showIcon && (
        <Button
          variant="unstyled-button"
          className={cx({
            'inline-input__icon': true,
            'inline-input__icon--large': variant === 'large',
          })}
          onClick={onRemove}
          tabIndex={-1}
          aria-label="Remove"
          data-qa-id={`${dataQaId || 'button'}-remove`}
        >
          <TrashIcon color={link} ratio={0.75} />
        </Button>
      )}
    </div>
  );
};
